import {Injectable} from '@angular/core';
import * as faceapi from '@vladmandic/face-api';

@Injectable({
  providedIn: 'root'
})
export class FaceApiService {
  readonly SSD_MOBILENETV1 = 'ssd_mobilenetv1';
  readonly TINY_FACE_DETECTOR = 'tiny_face_detector';

  forwardTimes: number[] = [];
  selectedFaceDetector?: string = this.SSD_MOBILENETV1;

  // ssd_mobilenetv1 options
  minConfidence = 0.5;

  // tiny_face_detector options
  inputSize = 512;
  scoreThreshold = 0.5;
  minFaceSize: any;
  withoutBoxes = false;

  loading = false;
  time?: string;
  fps?: string;

  faceDetectorSelect: any;
  inputSizeSelect: any;
  videoEl: any;

  constructor() {
  }

  clear() {
    this.selectedFaceDetector = this.SSD_MOBILENETV1;
    // ssd_mobilenetv1 options
    this.minConfidence = 0.5;
    // tiny_face_detector options
    this.inputSize = 512;
    this.scoreThreshold = 0.5;
    this.withoutBoxes = false;
  }

  stopStream() {
    if (this.videoEl && this.videoEl.srcObject) {
      this.videoEl.srcObject.getTracks().forEach((track: MediaStreamTrack) => {
        track.stop();
      });
    }
    this.videoEl.srcObject = undefined;
    this.videoEl = undefined;
    this.faceDetectorSelect = undefined;
    this.inputSizeSelect = undefined;
    this.selectedFaceDetector = undefined;
  }

  updateTimeStats(timeInMs: number) {
    this.forwardTimes = [timeInMs].concat(this.forwardTimes).slice(0, 30);
    const avgTimeInMs = this.forwardTimes.reduce((total, t) => total + t) / this.forwardTimes.length;
    this.time = Math.round(avgTimeInMs) + ' ms';
    this.fps = '' + faceapi.utils.round(1000 / avgTimeInMs);
  }

  getFaceDetectorOptions() {
    return this.selectedFaceDetector === this.SSD_MOBILENETV1
      ? new faceapi.SsdMobilenetv1Options({minConfidence: this.minConfidence})
      : new faceapi.TinyFaceDetectorOptions({inputSize: this.inputSize, scoreThreshold: this.scoreThreshold});
  }

  onIncreaseMinConfidence() {
    this.minConfidence = Math.min(faceapi.utils.round(this.minConfidence + 0.1), 1.0);
  }

  onDecreaseMinConfidence() {
    this.minConfidence = Math.max(faceapi.utils.round(this.minConfidence - 0.1), 0.1);
  }

  onInputSizeChanged(e: any) {
    this.changeInputSize(e.target.value);
  }

  changeInputSize(size: any) {
    this.inputSize = parseInt(size, 0);
    if (this.inputSizeSelect) {
      this.inputSizeSelect.value = String(this.inputSize);
    }
  }

  onIncreaseScoreThreshold() {
    this.scoreThreshold = Math.min(faceapi.utils.round(this.scoreThreshold + 0.1), 0.99);
  }

  onDecreaseScoreThreshold() {
    this.scoreThreshold = Math.max(faceapi.utils.round(this.scoreThreshold - 0.1), 0.1);
  }

  onIncreaseMinFaceSize() {
    this.minFaceSize = Math.min(faceapi.utils.round(this.minFaceSize + 20), 300);
  }

  onDecreaseMinFaceSize() {
    this.minFaceSize = Math.max(faceapi.utils.round(this.minFaceSize - 20), 50);
  }

  getCurrentFaceDetectionNet(): any {
    if (this.selectedFaceDetector === this.SSD_MOBILENETV1) {
      return faceapi.nets.ssdMobilenetv1;
    }
    if (this.selectedFaceDetector === this.TINY_FACE_DETECTOR) {
      return faceapi.nets.tinyFaceDetector;
    }
  }

  isFaceDetectionModelLoaded() {
    return !!this.getCurrentFaceDetectionNet().params;
  }

  async changeFaceDetector(detector: any) {
    this.selectedFaceDetector = detector;

    if (this.faceDetectorSelect) {
      this.faceDetectorSelect.value = detector;
    }
    this.loading = true;
    if (!this.isFaceDetectionModelLoaded()) {
      await this.getCurrentFaceDetectionNet().load('assets/faceapi/weights/');
    }
  }

  async onSelectedFaceDetectorChanged(e: any) {
    this.selectedFaceDetector = e.target.value;

    await this.changeFaceDetector(e.target.value);
  }

  init(faceDetectorSelect: any, inputSizeSelect: any, videoEl: any) {
    this.clear();
    this.videoEl = videoEl;
    this.faceDetectorSelect = faceDetectorSelect;
    this.inputSizeSelect = inputSizeSelect;
    if (faceDetectorSelect) {
      faceDetectorSelect.value = this.selectedFaceDetector;
    }
    if (inputSizeSelect) {
      inputSizeSelect.value = String(this.inputSize);
    }
  }

  async play(callback: any): Promise<any> {
    this.loading = false;

    if (this.videoEl) {
      if (this.videoEl.paused || this.videoEl.ended || !this.isFaceDetectionModelLoaded()) {
        return setTimeout(() => this.play(callback));
      }

      callback();

      setTimeout(() => this.play(callback));
    }
  }

}
